export const DEFAULT_BRAND_PRIMARY_COLOR = '#0070FF'
export const MOBILE_BRANDING_BAR_HEIGHT_PX = 56

export type ContractPageBranding = {
  favicon: string | null
  logo: string | null
  primary_color: string
  title: string | null
  contacts: string | null
}

export const EMPTY_CONTRACT_PAGE_BRANDING: ContractPageBranding = {
  favicon: null,
  logo: null,
  primary_color: DEFAULT_BRAND_PRIMARY_COLOR,
  title: null,
  contacts: null,
}

export function normalizeContractPageBranding(
  raw?: Partial<ContractPageBranding> | null
): ContractPageBranding {
  if (!raw) {
    return { ...EMPTY_CONTRACT_PAGE_BRANDING }
  }
  return {
    favicon: raw.favicon || null,
    logo: raw.logo || null,
    primary_color: raw.primary_color || DEFAULT_BRAND_PRIMARY_COLOR,
    title: raw.title?.trim() || null,
    contacts: raw.contacts?.trim() || null,
  }
}

export type UserProfileBrandingSource = {
  law_name?: string | null
  phone_number?: string | null
  additional_phone_number?: string | null
  email?: string | null
  law_address?: string | null
}

const MAX_BRANDING_TITLE_LENGTH = 120
const MAX_BRANDING_CONTACTS_LENGTH = 2000

export function deriveProfileBrandingDefaults(
  user?: UserProfileBrandingSource | null
): Pick<ContractPageBranding, 'title' | 'contacts'> {
  if (!user) {
    return { title: null, contacts: null }
  }

  const lawName = user.law_name?.trim()
  const title = lawName ? lawName.slice(0, MAX_BRANDING_TITLE_LENGTH) : null

  const contactLines: string[] = []
  const phone = (user.additional_phone_number || user.phone_number || '').trim()
  if (phone) {
    contactLines.push(`Тел.: ${phone}`)
  }
  const email = user.email?.trim()
  if (email) {
    contactLines.push(`Email: ${email}`)
  }
  const address = user.law_address?.trim()
  if (address) {
    contactLines.push(`Адрес: ${address}`)
  }

  let contacts = contactLines.length > 0 ? contactLines.join('\n') : null
  if (contacts && contacts.length > MAX_BRANDING_CONTACTS_LENGTH) {
    contacts = contacts.slice(0, MAX_BRANDING_CONTACTS_LENGTH)
  }

  return { title, contacts }
}

export function mergeBrandingWithProfileDefaults(
  branding: ContractPageBranding,
  user?: UserProfileBrandingSource | null
): ContractPageBranding {
  const defaults = deriveProfileBrandingDefaults(user)
  return {
    ...branding,
    title: branding.title || defaults.title,
    contacts: branding.contacts || defaults.contacts,
  }
}

export function hasActiveBranding(branding?: ContractPageBranding | null): boolean {
  if (!branding) {
    return false
  }
  return Boolean(
    branding.favicon ||
      branding.logo ||
      branding.title ||
      branding.contacts ||
      branding.primary_color !== DEFAULT_BRAND_PRIMARY_COLOR
  )
}

export function hasBrandingHeaderContent(branding?: ContractPageBranding | null): boolean {
  if (!branding) {
    return false
  }
  return Boolean(branding.logo || branding.title)
}

export function formatBrandingDocumentTitle(
  branding?: ContractPageBranding | null,
  documentName?: string | null
): string | null {
  const title = branding?.title?.trim()
  const docName = documentName?.trim()
  if (title && docName) {
    return `${title} — ${docName}`
  }
  if (title) {
    return title
  }
  return docName || null
}

export function applyFavicon(favicon: string | null | undefined): () => void {
  const link =
    (document.querySelector("link[rel*='icon']") as HTMLLinkElement | null) ||
    document.createElement('link')

  const previousHref = link.href
  const previousRel = link.rel

  link.rel = 'icon'
  if (favicon) {
    link.href = favicon
  } else {
    link.href = '/favicon.ico'
  }

  if (!link.parentNode) {
    document.head.appendChild(link)
  }

  return () => {
    link.rel = previousRel || 'icon'
    link.href = previousHref || '/favicon.ico'
  }
}

export function applyDocumentTitle(nextTitle: string | null | undefined): () => void {
  const previousTitle = document.title
  if (nextTitle) {
    document.title = nextTitle
  }
  return () => {
    document.title = previousTitle
  }
}

export function applyBrandCssVariables(
  primaryColor: string | undefined,
  root: HTMLElement = document.documentElement
): () => void {
  const previous = root.style.getPropertyValue('--contract-brand-primary')
  const color = primaryColor || DEFAULT_BRAND_PRIMARY_COLOR
  root.style.setProperty('--contract-brand-primary', color)
  return () => {
    if (previous) {
      root.style.setProperty('--contract-brand-primary', previous)
    } else {
      root.style.removeProperty('--contract-brand-primary')
    }
  }
}

export async function readImageAsDataUrl(file: File, maxBytes = 200_000): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Выберите файл изображения')
  }
  if (file.size > maxBytes) {
    throw new Error('Файл слишком большой. Максимум 200 КБ')
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.readAsDataURL(file)
  })
}

export function brandingCssClass(enabled: boolean): string {
  return enabled ? 'contract-page-branded' : ''
}
